/**
 * EditTaxDialog — the rich Tax-and-invoicing-defaults section,
 * Phase C (migration 0030). All six rows persist:
 *
 *   1. Tax mode — CGST+SGST / IGST / Zero-rated / Exempt. CGST vs
 *      IGST is auto-derivable from the customer's state; the explicit
 *      override exists for export/SEZ + exempt classifications.
 *   2. Default HSN/SAC — pre-fill for the invoice composer.
 *   3. Default GST rate % — pre-fill on new line items.
 *   4. E-invoice required (toggle).
 *   5. TDS applicable + section (the original V1.0 fields).
 *   6. E-way bill required (toggle).
 *
 * @see prototype/sales/customer-new.html §5 — visual baseline
 * @see packages/db/migrations/0030_customer_phase_c.sql
 */
import * as React from "react";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;
type TaxMode = "cgst_sgst" | "igst" | "zero_rated" | "exempt";
type TdsSection = "194C" | "194J" | "194Q";

const TAX_MODES: ReadonlyArray<{ value: TaxMode; label: React.ReactNode; subtitle: string }> = [
  {
    value: "cgst_sgst",
    label: (
      <>
        CGST + SGST <span className="text-muted font-mono ml-1">9% + 9%</span>
      </>
    ),
    subtitle: "Intra-state (same-state buyer)",
  },
  {
    value: "igst",
    label: (
      <>
        IGST <span className="text-muted font-mono ml-1">18%</span>
      </>
    ),
    subtitle: "Inter-state (different state)",
  },
  { value: "zero_rated", label: "Zero-rated", subtitle: "Export / SEZ supply" },
  { value: "exempt", label: "Exempt", subtitle: "Goods/services outside GST" },
];

const GST_RATES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "0", label: "0%" },
  { value: "5", label: "5%" },
  { value: "12", label: "12%" },
  { value: "18", label: "18%" },
  { value: "28", label: "28%" },
];

const TDS_SECTIONS: ReadonlyArray<{ value: TdsSection; label: string }> = [
  { value: "194C", label: "194C — Contractor / sub-contractor (1–2%)" },
  { value: "194J", label: "194J — Professional / technical (2–10%)" },
  { value: "194Q", label: "194Q — Purchase of goods (0.1%)" },
];

export interface EditTaxDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: {
    taxMode?: TaxMode;
    defaultHsn?: string;
    defaultGstRatePct?: number | null;
    eInvoiceRequired?: boolean;
    isTdsApplicable?: boolean;
    tdsSection?: TdsSection | null;
    eWayBillRequired?: boolean;
  }) => Promise<void>;
}

export function EditTaxDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditTaxDialogProps) {
  const [taxMode, setTaxMode] = React.useState<TaxMode>(customer.taxMode as TaxMode);
  const [hsn, setHsn] = React.useState(customer.defaultHsn ?? "");
  const [gstRate, setGstRate] = React.useState<string>(
    customer.defaultGstRatePct === null ? "" : String(customer.defaultGstRatePct),
  );
  const [eInvoice, setEInvoice] = React.useState(customer.eInvoiceRequired);
  const [eWayBill, setEWayBill] = React.useState(customer.eWayBillRequired);
  const [tdsApplicable, setTdsApplicable] = React.useState(customer.isTdsApplicable);
  const [tdsSec, setTdsSec] = React.useState<string>(customer.tdsSection ?? "");

  React.useEffect(() => {
    if (open) {
      setTaxMode(customer.taxMode as TaxMode);
      setHsn(customer.defaultHsn ?? "");
      setGstRate(customer.defaultGstRatePct === null ? "" : String(customer.defaultGstRatePct));
      setEInvoice(customer.eInvoiceRequired);
      setEWayBill(customer.eWayBillRequired);
      setTdsApplicable(customer.isTdsApplicable);
      setTdsSec(customer.tdsSection ?? "");
    }
  }, [open, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Parameters<EditTaxDialogProps["onSave"]>[0] = {};

    if (taxMode !== customer.taxMode) patch.taxMode = taxMode;

    const trimmedHsn = hsn.trim();
    if (trimmedHsn !== (customer.defaultHsn ?? "")) patch.defaultHsn = trimmedHsn;

    const nextRate: number | null = gstRate === "" ? null : Number.parseInt(gstRate, 10);
    if (nextRate !== customer.defaultGstRatePct) patch.defaultGstRatePct = nextRate;

    if (eInvoice !== customer.eInvoiceRequired) patch.eInvoiceRequired = eInvoice;
    if (eWayBill !== customer.eWayBillRequired) patch.eWayBillRequired = eWayBill;

    if (tdsApplicable !== customer.isTdsApplicable) patch.isTdsApplicable = tdsApplicable;
    if (tdsApplicable) {
      if (tdsSec && tdsSec !== (customer.tdsSection ?? "")) {
        patch.tdsSection = tdsSec as TdsSection;
      }
    } else if (customer.tdsSection !== null) {
      patch.tdsSection = null;
    }

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit tax & invoicing defaults"
      description="Pre-fills every new invoice for this customer. Operators can still override per invoice."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-xl"
    >
      <SectionField
        label="Default tax mode"
        hint={
          taxMode === "cgst_sgst" || taxMode === "igst"
            ? "CGST+SGST vs IGST is normally auto-derived from the buyer's state. Override only if you know better."
            : "Zero-rated for export / SEZ supply; Exempt for goods/services outside GST."
        }
      >
        <div className="grid grid-cols-2 gap-2">
          {TAX_MODES.map((m) => (
            <TaxModeTile
              key={m.value}
              active={taxMode === m.value}
              onClick={() => setTaxMode(m.value)}
              label={m.label}
              subtitle={m.subtitle}
            />
          ))}
        </div>
      </SectionField>

      <div className="grid grid-cols-2 gap-3">
        <SectionField
          label="Default HSN / SAC"
          hint="4–8 digits. If most invoices ship the same SKU class, set it here."
        >
          <Input
            value={hsn}
            onChange={(e) => setHsn(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="e.g., 73181500"
            inputMode="numeric"
            className="font-mono tabular-nums"
            maxLength={8}
          />
        </SectionField>

        <SectionField
          label="Default GST rate"
          hint="Applied to new line items unless the SKU overrides."
        >
          <Select value={gstRate} onValueChange={setGstRate}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a rate…" />
            </SelectTrigger>
            <SelectContent>
              {GST_RATES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SectionField>
      </div>

      <ToggleRow
        title="E-invoice (IRN) required"
        body="Mandatory for company turnover > ₹5 Cr. Flint generates the IRN via NIC IRP before sending. Auto-on once your AATO crosses the threshold."
        checked={eInvoice}
        onChange={setEInvoice}
        label="E-invoice toggle"
      />

      <ToggleRow
        title="Customer deducts TDS on collection"
        body="When the buyer pays you, they withhold TDS under the section below. Flint pre-fills the deduction on receipts so the short payment reconciles to TDS-receivable automatically."
        checked={tdsApplicable}
        onChange={(next) => {
          setTdsApplicable(next);
          if (!next) setTdsSec("");
        }}
        label="TDS-on-collection toggle"
      />

      {tdsApplicable ? (
        <div className="-mt-2">
          <SectionField
            label="TDS section"
            required
            hint="Determines the deduction rate the receipt records."
          >
            <Select {...(tdsSec ? { value: tdsSec } : {})} onValueChange={(v) => setTdsSec(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select section…" />
              </SelectTrigger>
              <SelectContent>
                {TDS_SECTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SectionField>
        </div>
      ) : null}

      <ToggleRow
        title="E-way bill on dispatch"
        body="Required for inter-state movement of goods > ₹50,000. Flint auto-generates from the invoice + vehicle number on the dispatch screen."
        checked={eWayBill}
        onChange={setEWayBill}
        label="E-way bill toggle"
      />
    </EditDialog>
  );
}

/**
 * 4-up tile for the tax-mode picker. Active state uses the same
 * sky-toned highlight as the rest of the inline-edit affordances.
 */
function TaxModeTile({
  active,
  onClick,
  label,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  label: React.ReactNode;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-col gap-0.5 items-start text-left px-3 py-2 rounded-md border transition-colors",
        active
          ? "border-sky-600 bg-sky-50 text-ink"
          : "border-line bg-paper text-ink-2 hover:bg-bg",
      )}
    >
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-xs text-muted leading-tight">{subtitle}</span>
    </button>
  );
}

/**
 * Toggle row with title/body/switch. Mirrors the dead form's
 * `ToggleRow` visually but lighter — no extra CSS, plain checkbox
 * styled as a chip on the right.
 */
function ToggleRow({
  title,
  body,
  checked,
  onChange,
  label,
}: {
  title: string;
  body: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer rounded-md border border-line bg-paper p-3 hover:bg-bg/40 transition-colors"
      aria-label={label}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink leading-snug">{title}</div>
        <div className="text-xs text-muted leading-snug mt-0.5">{body}</div>
      </div>
      <span className="flex-shrink-0 pt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
      </span>
    </label>
  );
}
